// O(1) read accelerators over the flat store arrays (boxes / containers /
// columns / masks). Each derived Map is cached against the array *reference*
// in a WeakMap. Between two `produce` calls that reference is immutable — immer
// hands out a fresh array whenever the contents change — so a cache entry stays
// valid exactly until its array changes, then is rebuilt lazily on first access
// (once per render pass, shared across every component). Mid-`produce` that
// immutability does not hold; see cacheIfStable below.
//
// These are pure lookups: no store state, no subscriptions, no extra renders.
// They replace the O(n) `Array.find/filter/some(... === id ...)` scans that ran
// on every node mount, which were O(n^2) across the ~1238 sequential container
// mounts of a PDF build.

import { isDraft } from 'immer';

const EMPTY = [];

const byIdCache = new WeakMap();
const containerByColumnCache = new WeakMap();
const boxesByColumnCache = new WeakMap();
const maskBoxByIdCache = new WeakMap();

// Mutators run these lookups on `draft.columns` & co. mid-`produce`, where the
// "one array reference per version" invariant above does NOT hold: a mutator
// that rebuilds an array (`draft.columns = draft.columns.concat(...)`) hands
// immer a plain array holding *drafts*, and immer finalizes it IN PLACE — the
// very same array object then becomes the next state. A Map cached against it
// during the produce therefore survives into the new state while still holding
// the drafts, which immer revokes on exit ("Cannot perform 'get' on a proxy
// that has been revoked" on the next read).
//
// So: build the Map as usual, but only cache it if no draft went into it. Costs
// one `isDraft` per item at build time, i.e. once per array version, never on a
// cache hit.
const cacheIfStable = (cache, arr, map, sawDraft) => {
  if (!sawDraft) cache.set(arr, map);
  return map;
};

// Map<id, item> for any array of `{ id }` objects.
export const byId = (arr) => {
  const cached = byIdCache.get(arr);
  if (cached) return cached;
  const map = new Map();
  let sawDraft = false;
  for (const item of arr) {
    if (!sawDraft && isDraft(item)) sawDraft = true;
    map.set(item.id, item);
  }
  return cacheIfStable(byIdCache, arr, map, sawDraft);
};

export const getContainerById = (containers, id) => byId(containers).get(id);

export const getColumnById = (columns, id) => byId(columns).get(id);

export const getMaskById = (masks, id) => byId(masks).get(id);

// Container that owns a given columnId. A column belongs to exactly one
// container, so this matches `containers.find(c => c.columnsIds.includes(id))`.
export const getContainerByColumnId = (containers, columnId) => {
  let map = containerByColumnCache.get(containers);
  if (!map) {
    map = new Map();
    let sawDraft = false;
    for (const c of containers) {
      if (!sawDraft && isDraft(c)) sawDraft = true;
      for (const cid of c.columnsIds) map.set(cid, c);
    }
    cacheIfStable(containerByColumnCache, containers, map, sawDraft);
  }
  return map.get(columnId);
};

// Store boxes grouped by columnId (NOT mask boxes). Lists preserve store-array
// order. Returns a stable reference per boxes array — better for memoisation
// than `boxes.filter(...)`, which allocated a new array every render.
export const getBoxesByColumnId = (boxes, columnId) => {
  let map = boxesByColumnCache.get(boxes);
  if (!map) {
    map = new Map();
    let sawDraft = false;
    for (const b of boxes) {
      if (!sawDraft && isDraft(b)) sawDraft = true;
      const list = map.get(b.columnId);
      if (list) list.push(b);
      else map.set(b.columnId, [b]);
    }
    cacheIfStable(boxesByColumnCache, boxes, map, sawDraft);
  }
  return map.get(columnId) || EMPTY;
};

// Box by id, searching store boxes first then mask boxes — matches the
// `boxes.find(...) ?? masks[].boxes.find(...)` fallback used across components.
export const getBoxById = (boxes, masks, id) => {
  const direct = byId(boxes).get(id);
  if (direct) return direct;
  let map = maskBoxByIdCache.get(masks);
  if (!map) {
    map = new Map();
    let sawDraft = false;
    for (const mask of masks) {
      // Le masque lui-même est testé : un masque sans box ne ferait entrer
      // aucun draft dans la Map, mais suffit à rendre le tableau instable.
      if (!sawDraft && isDraft(mask)) sawDraft = true;
      if (!mask?.boxes) continue;
      for (const b of mask.boxes) {
        if (!sawDraft && isDraft(b)) sawDraft = true;
        map.set(b.id, b);
      }
    }
    cacheIfStable(maskBoxByIdCache, masks, map, sawDraft);
  }
  return map.get(id);
};
